/**
 * The viewer's memory of what it has shown (#1069 Story 8.3; FR83, NFR65).
 *
 * A pure store, no React: a most-recently-used map from file id to the bytes
 * `fetchMessageFile` returned, bounded by DECODED bytes, with the in-flight
 * fetches folded in so that "each file requested at most once PER EPOCH" is
 * true by construction rather than by timing (D4). Per epoch: a `clear()`
 * across a live fetch lets the next ask start a second download while the
 * first is discarded by the epoch; the panel's own "abandoned logically, not
 * cancelled" rule, seen from the store. `useMessageFileCache` owns one of
 * these per panel and empties it on every lifetime edge; nothing else holds a
 * reference.
 *
 * ⚠️ CHARGED BY DECODED BYTES, AND THERE IS NO RAW TWIN (C4). Every other
 * ceiling in `messageFileLimits.ts` says in capitals that the decoded number
 * is the wrong one to bound memory with. That is true of base64 that ARRIVES
 * from a sender who can pad it. This base64 is produced by our own `toBase64`
 * from the decoded bytes in an exact 4:3 ratio, so one number bounds both;
 * `FetchedMessageFile.byteLength` is read from the response for free, and
 * nothing here walks the string.
 *
 * ⚠️ LEAST RECENTLY TOUCHED, NOT LEAST RECENTLY SHOWN (D6). NFR65 says
 * "least-recently-shown", but a prefetched neighbour has never been shown,
 * and ordering by show time alone would evict it first at the ceiling, before
 * the reader stepped onto it. A touch is a show OR an insert: `Map` insertion
 * order with delete-and-reinsert on every touch (the `capStagedThreads`
 * shape). The caller names the file on screen with `keep`, and it is never
 * evicted however old. ⚠️ `keep` IS RESOLVED WHEN THE BYTES LAND, NOT WHEN
 * THE LOAD STARTED (review, 2026-09-21): a prefetch started with the previous
 * file on screen lands after the reader has stepped on, and the file it must
 * not evict is the one on screen NOW. So every `getOrFetch` and every `peek`
 * that names a `keep` moves the store's own notion of "on screen", and the
 * insert reads that, never the options captured at start.
 *
 * ⚠️ THE EPOCH IS WHAT KEEPS ONE CONVERSATION'S FILE OUT OF THE NEXT ONE'S
 * CACHE (D3, D4). `clear()` bumps it; a loader that resolves under an older
 * epoch hands its value to whoever is already awaiting it (they will fail
 * their own `current()` check) and is NOT inserted. Without it, a thread
 * switch mid-prefetch would repopulate the new conversation's cache with the
 * previous conversation's file, which is the one thing the authorization
 * below this store must never be seen to allow.
 *
 * ⚠️ NOT REACT QUERY (D1). The app-wide client is what every 401 site clears
 * and what `gcTime` garbage-collects, neither of which was written with a
 * 100 MB entry set in mind. And not module scope: a store that outlived the
 * panel would outlive the request and the session.
 *
 * @see src/services/correspondence/__tests__/messageFileCache.test.ts
 */
import type { FetchedMessageFile } from './messageFileBytes.fetch';

export interface MessageFileCache {
  /**
   * The cached value, if any. A show is a touch: a hit moves to the back.
   * `keep` names the file now on screen (a hit IS a show).
   */
  peek: (
    fileId: string,
    options?: { keep?: string },
  ) => FetchedMessageFile | undefined;
  /**
   * The cached value, the promise already in flight for this id, or a fresh
   * load. `keep` is the id that must survive eviction (the file on screen).
   */
  getOrFetch: (
    fileId: string,
    loader: () => Promise<FetchedMessageFile>,
    options?: { keep?: string },
  ) => Promise<FetchedMessageFile>;
  /**
   * The promise already in flight for this id, or `undefined`. A way to wait
   * on a load someone else started without starting one (the download path,
   * D5: it reads and joins, and never inserts on its own).
   */
  join: (fileId: string) => Promise<FetchedMessageFile> | undefined;
  /** True only for bytes held (not for a fetch in flight). */
  has: (fileId: string) => boolean;
  /** True while a load for this id has been started and not settled. */
  inFlight: (fileId: string) => boolean;
  /** Decoded bytes currently held. */
  sizeBytes: () => number;
  /** Drop everything held and forget every load in flight (D4's epoch). */
  clear: () => void;
}

export const createMessageFileCache = ({
  ceilingBytes,
}: {
  ceilingBytes: number;
}): MessageFileCache => {
  const held = new Map<string, FetchedMessageFile>();
  const loading = new Map<string, Promise<FetchedMessageFile>>();
  let bytes = 0;
  let epoch = 0;
  /** The file on screen, by the last caller that said so. */
  let kept: string | undefined;

  const touch = (fileId: string, value: FetchedMessageFile) => {
    held.delete(fileId);
    held.set(fileId, value);
  };

  /** Oldest first, never the file on screen, until under the ceiling. */
  const evict = () => {
    for (const [id, value] of held) {
      if (bytes <= ceilingBytes) break;
      if (id === kept) continue;
      held.delete(id);
      bytes -= value.byteLength;
    }
  };

  const insert = (fileId: string, value: FetchedMessageFile) => {
    // A file larger than the whole ceiling on its own is shown and not
    // stored (D6): storing it would evict everything else for one entry.
    // ⚠️ AND A LENGTH THAT IS NOT A NUMBER IS TREATED THE SAME WAY. One
    // `undefined` added to `bytes` makes it NaN for the life of the store,
    // and `NaN <= ceiling` is false, so every later insert would evict
    // everything but the file on screen and nothing would say so.
    if (!Number.isFinite(value.byteLength) || value.byteLength > ceilingBytes)
      return;
    if (held.has(fileId)) {
      bytes -= held.get(fileId)!.byteLength;
    }
    bytes += value.byteLength;
    touch(fileId, value);
    evict();
  };

  const peek: MessageFileCache['peek'] = (fileId, options) => {
    if (options?.keep !== undefined) kept = options.keep;
    const value = held.get(fileId);
    if (value) touch(fileId, value);
    return value;
  };

  const getOrFetch: MessageFileCache['getOrFetch'] = (
    fileId,
    loader,
    options,
  ) => {
    const hit = peek(fileId, options);
    if (hit) return Promise.resolve(hit);
    const joined = loading.get(fileId);
    if (joined) return joined;

    const startedIn = epoch;
    const promise = loader().then(
      value => {
        // ⚠️ THE EPOCH CHECK. `clear()` already forgot this entry; do not
        // put it back, and do not insert a value that belongs to the
        // conversation the reader has left.
        if (startedIn === epoch) {
          loading.delete(fileId);
          insert(fileId, value);
        }
        return value;
      },
      (error: unknown) => {
        // A failed load is forgotten so the next ask starts a fresh one
        // (D2); every joined caller sees this same rejection.
        if (startedIn === epoch) loading.delete(fileId);
        throw error;
      },
    );
    loading.set(fileId, promise);
    return promise;
  };

  return {
    peek,
    getOrFetch,
    join: fileId => loading.get(fileId),
    has: fileId => held.has(fileId),
    inFlight: fileId => loading.has(fileId),
    sizeBytes: () => bytes,
    clear: () => {
      held.clear();
      loading.clear();
      bytes = 0;
      kept = undefined;
      epoch += 1;
    },
  };
};
